import React, { useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Webcam from "react-webcam";
import {
  FaTimes,
  FaCamera,
  FaRedo,
  FaCheck,
  FaSearch,
  FaIdCard,
  FaUserCheck,
  FaExclamationCircle,
} from "react-icons/fa";
import { RiUserAddFill } from "react-icons/ri";
import { MdPersonSearch } from "react-icons/md";
import getOperatorByAadhaar from "../hooks/getOperatorByAadhaar";

// ─── Field class constants ──────────────────────────────────────────────────
const FIELD_CLASS =
  "w-full px-3 py-2.5 rounded-xl text-sm font-semibold text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 transition";
const FIELD_STYLE = { background: "#ffffff", border: "1.5px solid #e5e7eb" };

const validationSchema = Yup.object({
  operator_name: Yup.string().required("Name is required"),
  operator_mobile: Yup.string()
    .matches(/^[6-9]\d{9}$/, "Invalid mobile number")
    .required("Mobile number is required"),
  operator_aadhaar: Yup.string()
    .matches(/^\d{12}$/, "Aadhaar must be 12 digits")
    .required("Aadhaar number is required"),
  operator_photo: Yup.string(),
});

const getOperatorPhotoUrl = (photo, eventId) => {
  if (!photo) return "";
  if (
    photo.startsWith("data:") ||
    photo.startsWith("http://") ||
    photo.startsWith("https://")
  ) {
    return photo;
  }
  const cleanBase = (process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "");
  
  if (
    !photo.includes("/app/media/") &&
    !photo.includes("app/media/") &&
    !photo.includes("/media/") &&
    !photo.includes("media/") &&
    eventId
  ) {
    return `${cleanBase}/app/media/events/${eventId}/${photo}`;
  }
  
  const cleanPath = photo.startsWith("/") ? photo : `/${photo}`;
  return `${cleanBase}${cleanPath}`;
};

// ─── Operator info card ──────────────────────────────────────────────────────
function OperatorInfoCard({ operator, aadhaar }) {
  const name = operator?.operator_name || operator?.name || "—";
  const mobile = operator?.operator_mobile || operator?.mobile || "—";
  const photo =
    operator?.operator_photo || operator?.photo || null;

  const initials = name
    .trim()
    .split(" ")
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const maskedAadhaar = aadhaar
    ? `xxxx xxxx ${aadhaar.slice(-4)}`
    : "—";

  return (
    <div
      className="rounded-2xl overflow-hidden border"
      style={{
        border: "1.5px solid rgba(34,197,94,0.35)",
        background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
      }}
    >
      {/* Verified banner */}
      <div
        className="flex items-center gap-2 px-4 py-2"
        style={{ background: "rgba(34,197,94,0.12)" }}
      >
        <FaUserCheck className="text-emerald-600 text-sm shrink-0" />
        <span className="text-xs font-black text-emerald-700 uppercase tracking-wider">
          Operator Found · Verified
        </span>
      </div>

      {/* Details row */}
      <div className="flex items-center gap-4 px-4 py-4">
        {/* Avatar */}
        {photo ? (
          <img
            src={getOperatorPhotoUrl(photo, operator?.event_id)}
            alt={name}
            className="w-16 h-16 rounded-xl object-cover shrink-0 border-2 border-emerald-200"
          />
        ) : (
          <div
            className="w-16 h-16 rounded-xl flex items-center justify-center shrink-0 text-white font-black text-xl"
            style={{ background: "linear-gradient(135deg, #FF8C00, #E07800)" }}
          >
            {initials}
          </div>
        )}

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-base font-black text-gray-900 truncate">{name}</p>
          <p className="text-sm text-gray-500 mt-0.5 font-medium">📱 {mobile}</p>
          <p className="text-xs text-gray-400 mt-1 font-mono">
            🪪 {maskedAadhaar}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Lookup tab ───────────────────────────────────────────────────────────────
function LookupTab({ onAssign }) {
  const [aadhaar, setAadhaar] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | found | notfound
  const [foundOperator, setFoundOperator] = useState(null);

  const handleInput = async (value) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 12);
    setAadhaar(cleaned);
    setFoundOperator(null);

    if (cleaned.length < 12) {
      setStatus("idle");
      return;
    }

    setStatus("loading");
    const result = await getOperatorByAadhaar(cleaned);
    if (result) {
      setFoundOperator(result);
      setStatus("found");
    } else {
      setStatus("notfound");
    }
  };

  const handleManualSearch = async () => {
    if (aadhaar.length !== 12) return;
    setStatus("loading");
    const result = await getOperatorByAadhaar(aadhaar);
    if (result) {
      setFoundOperator(result);
      setStatus("found");
    } else {
      setStatus("notfound");
    }
  };

  const handleAssignClick = () => {
    if (!foundOperator) return;
    onAssign({
      operator_name:
        foundOperator.operator_name || foundOperator.name || "",
      operator_mobile:
        foundOperator.operator_mobile || foundOperator.mobile || "",
      operator_aadhaar: aadhaar,
      operator_photo:
        foundOperator.operator_photo || foundOperator.photo || "",
    });
  };

  return (
    <div className="px-6 py-5 space-y-5">
      {/* Aadhaar input */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-bold text-gray-500 uppercase tracking-widest flex items-center gap-1.5">
          <FaIdCard className="text-orange-500" />
          Aadhaar Number
        </label>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              inputMode="numeric"
              value={aadhaar}
              onChange={(e) => handleInput(e.target.value)}
              maxLength={12}
              placeholder="Enter 12-digit Aadhaar"
              className={`${FIELD_CLASS} pr-10 font-mono tracking-widest`}
              style={FIELD_STYLE}
            />
            {/* Digit counter */}
            <span
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold tabular-nums"
              style={{ color: aadhaar.length === 12 ? "#16a34a" : "#9ca3af" }}
            >
              {aadhaar.length}/12
            </span>
          </div>
          <button
            type="button"
            onClick={handleManualSearch}
            disabled={aadhaar.length !== 12 || status === "loading"}
            className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: "linear-gradient(135deg, #FF8C00, #E07800)",
              boxShadow: "0 4px 12px rgba(255,140,0,0.3)",
            }}
            title="Search operator"
          >
            {status === "loading" ? (
              <svg className="w-4 h-4 text-white animate-spin" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="40" strokeDashoffset="15" strokeLinecap="round" />
              </svg>
            ) : (
              <FaSearch className="text-white text-sm" />
            )}
          </button>
        </div>
        <p className="text-[11px] text-gray-400">
          Auto-searches when all 12 digits are entered
        </p>
      </div>

      {/* Loading state */}
      {status === "loading" && (
        <div className="flex items-center gap-3 rounded-xl px-4 py-3 bg-orange-50 border border-orange-100">
          <svg className="w-4 h-4 text-orange-500 animate-spin shrink-0" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="40" strokeDashoffset="15" strokeLinecap="round" />
          </svg>
          <p className="text-sm font-semibold text-orange-700">
            Searching for operator…
          </p>
        </div>
      )}

      {/* Found — operator info card + assign */}
      {status === "found" && foundOperator && (
        <div className="space-y-3">
          <OperatorInfoCard operator={foundOperator} aadhaar={aadhaar} />

          <button
            type="button"
            onClick={handleAssignClick}
            className="w-full py-3 rounded-xl text-white text-sm font-black tracking-wide flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all"
            style={{
              background: "linear-gradient(135deg, #16a34a, #15803d)",
              boxShadow: "0 4px 16px rgba(22,163,74,0.35)",
            }}
          >
            <FaCheck />
            Assign to Event
          </button>
        </div>
      )}

      {/* Not found */}
      {status === "notfound" && (
        <div className="flex items-start gap-3 rounded-xl px-4 py-3 bg-red-50 border border-red-100">
          <FaExclamationCircle className="text-red-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-bold text-red-700">Operator not registered</p>
            <p className="text-xs text-red-500 mt-0.5">
              No operator found with Aadhaar {aadhaar.slice(0, 4)}xxxx{aadhaar.slice(-4)}.
              Use the <span className="font-bold">"Add Manually"</span> tab to register them.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Manual tab (existing form) ───────────────────────────────────────────────
function ManualTab({ onSubmit, onClose }) {
  const webcamRef = useRef(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);

  const capturePhoto = (setFieldValue) => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedPhoto(imageSrc);
    setFieldValue("operator_photo", imageSrc);
    setCameraActive(false);
  };

  const retake = (setFieldValue) => {
    setCapturedPhoto(null);
    setFieldValue("operator_photo", "");
    setCameraActive(true);
  };

  return (
    <Formik
      initialValues={{
        operator_name: "",
        operator_mobile: "",
        operator_aadhaar: "",
        operator_photo: "",
      }}
      validationSchema={validationSchema}
      onSubmit={(values, { resetForm }) => {
        onSubmit(values);
        resetForm();
        setCapturedPhoto(null);
        setCameraActive(false);
        onClose();
      }}
    >
      {({ setFieldValue }) => (
        <Form className="px-6 py-5 space-y-4">
          <FormField
            name="operator_name"
            label="Operator Name"
            type="text"
            placeholder="Full name"
            fieldClass={`${FIELD_CLASS} no-translate`}
            fieldStyle={FIELD_STYLE}
          />
          <FormField
            name="operator_mobile"
            label="Mobile Number"
            type="text"
            placeholder="10-digit mobile"
            fieldClass={FIELD_CLASS}
            fieldStyle={FIELD_STYLE}
          />
          <FormField
            name="operator_aadhaar"
            label="Aadhaar Number"
            type="text"
            placeholder="12-digit Aadhaar"
            fieldClass={`${FIELD_CLASS} font-mono tracking-widest`}
            fieldStyle={FIELD_STYLE}
          />

          {/* Live Photo */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">
              Live Photo
            </label>
            <div className="rounded-2xl overflow-hidden border border-gray-200 bg-gray-50">
              {capturedPhoto ? (
                <div className="relative">
                  <img
                    src={capturedPhoto}
                    alt="Captured"
                    className="w-full object-cover rounded-t-xl"
                    style={{ maxHeight: "200px" }}
                  />
                  <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 border border-emerald-200">
                    <FaCheck className="text-emerald-600 text-xs" />
                    <span className="text-emerald-700 text-xs font-bold">Captured</span>
                  </div>
                </div>
              ) : cameraActive ? (
                <div className="relative">
                  <Webcam
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    className="w-full object-cover rounded-t-xl"
                    style={{ maxHeight: "200px" }}
                  />
                  <button
                    type="button"
                    onClick={() => capturePhoto(setFieldValue)}
                    className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-6 py-2.5 bg-orange-600 text-white text-sm font-bold rounded-full shadow-xl hover:bg-orange-700 transition active:scale-95"
                  >
                    <FaCamera /> Capture
                  </button>
                </div>
              ) : (
                <div className="py-10 flex flex-col items-center justify-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-orange-50 flex items-center justify-center text-orange-400">
                    <FaCamera size={24} />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-bold text-gray-800">Camera Access Required</p>
                    <p className="text-xs text-gray-500 mt-1 max-w-[200px]">
                      Allow camera access to take a live photo of the Operator.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCameraActive(true)}
                    className="px-5 py-2 bg-orange-600 text-white text-xs font-black rounded-xl hover:bg-orange-700 shadow-lg shadow-orange-200 transition"
                  >
                    Open Camera
                  </button>
                </div>
              )}

              {capturedPhoto && (
                <div className="px-4 py-3 bg-white border-t border-gray-100 flex justify-between items-center">
                  <p className="text-[10px] font-medium text-gray-400">
                    Photo verified for identity
                  </p>
                  <button
                    type="button"
                    onClick={() => retake(setFieldValue)}
                    className="flex items-center gap-1.5 text-xs font-bold text-orange-600 hover:text-orange-700"
                  >
                    <FaRedo size={10} /> Retake
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              style={{
                background: "linear-gradient(135deg, #FF8C00, #E07800)",
                boxShadow: "0 4px 16px rgba(255,140,0,0.3)",
              }}
              className="w-full py-3 text-white text-sm font-black rounded-xl hover:opacity-90 active:scale-[0.98] transition-all tracking-wide flex items-center justify-center gap-2"
            >
              <RiUserAddFill />
              Add Operator Access
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
const HandleEventOperatorModal = ({ isOpen, onClose, onSubmit }) => {
  const [activeTab, setActiveTab] = useState("lookup");

  if (!isOpen) return null;

  const handleAssign = (data) => {
    onSubmit(data);
    onClose();
  };

  const tabs = [
    { id: "lookup", label: "Lookup by Aadhaar", icon: <MdPersonSearch className="text-base" /> },
    { id: "manual", label: "Add Manually",      icon: <RiUserAddFill  className="text-sm" /> },
  ];

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-gray-900/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <style>{`
        @keyframes opModalUp {
          from { opacity: 0; transform: translateY(20px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .op-modal-scroll::-webkit-scrollbar { width: 4px; }
        .op-modal-scroll::-webkit-scrollbar-track { background: transparent; }
        .op-modal-scroll::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 99px; }
      `}</style>

      <div
        style={{
          background: "#ffffff",
          animation: "opModalUp 0.3s cubic-bezier(0.22,1,0.36,1) both",
          maxHeight: "92vh",
        }}
        className="w-full max-w-lg rounded-2xl overflow-hidden flex flex-col shadow-2xl border border-gray-100"
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 shrink-0 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-orange-100 border border-orange-200">
              <RiUserAddFill className="text-orange-600 text-base" />
            </div>
            <div>
              <p className="text-xs font-bold text-orange-600 uppercase tracking-widest mb-0.5">
                Operators
              </p>
              <h2 className="text-lg font-black text-gray-900 leading-none">
                Add Operator to Event
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
          >
            <FaTimes className="text-sm" />
          </button>
        </div>

        {/* ── Tab bar ── */}
        <div className="flex px-6 pt-4 pb-0 gap-2 shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${
                activeTab === tab.id
                  ? "text-white shadow-md"
                  : "text-gray-500 bg-gray-100 hover:bg-gray-200"
              }`}
              style={
                activeTab === tab.id
                  ? {
                      background: "linear-gradient(135deg, #FF8C00, #E07800)",
                      boxShadow: "0 4px 12px rgba(255,140,0,0.28)",
                    }
                  : {}
              }
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Divider ── */}
        <div className="mx-6 mt-4 h-px bg-gray-100 shrink-0" />

        {/* ── Tab content ── */}
        <div className="overflow-y-auto flex-1 op-modal-scroll">
          {activeTab === "lookup" ? (
            <LookupTab onAssign={handleAssign} />
          ) : (
            <ManualTab onSubmit={onSubmit} onClose={onClose} />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Shared form field ────────────────────────────────────────────────────────
const FormField = ({ name, label, type, placeholder, fieldClass, fieldStyle }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">
      {label}
    </label>
    <Field
      name={name}
      type={type}
      placeholder={placeholder}
      className={fieldClass}
      style={fieldStyle}
    />
    <ErrorMessage
      name={name}
      component="p"
      className="text-red-500 text-[10px] font-bold mt-1"
    />
  </div>
);

export default HandleEventOperatorModal;
